/**
 * Promise-based confirmation dialog. Replaces the ad-hoc `<ConfirmModal>` +
 * local `useState` pattern that lives in every admin page.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Eliminar centro', message: '…', tone: 'danger' })) {
 *     await deleteCenter(...);
 *   }
 *
 * Single mount in `AdminLayout`. The provider holds at most one open dialog;
 * a new `confirm()` call while one is open will queue the second after the
 * first resolves (rare in practice — the UI usually blocks during the modal).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ConfirmModal, type ConfirmTone } from '@components/admin/ConfirmModal';

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingDialog extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<PendingDialog | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setDialog({ ...opts, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (!dialog) return;
    dialog.resolve(true);
    setDialog(null);
  }, [dialog]);

  const handleCancel = useCallback(() => {
    if (!dialog) return;
    dialog.resolve(false);
    setDialog(null);
  }, [dialog]);

  // Stable value ref so consumers don't re-render on dialog state changes.
  const ctxValue = useMemo<ConfirmFn>(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={ctxValue}>
      {children}
      <ConfirmModal
        open={dialog !== null}
        title={dialog?.title ?? ''}
        message={dialog?.message ?? ''}
        confirmLabel={dialog?.confirmLabel}
        cancelLabel={dialog?.cancelLabel}
        tone={dialog?.tone}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
